import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User } from '@api/api';
import { endpoints } from '../api/endpoints';
import { isApiError, setAuthHandlers } from '../api/client';

export type AuthStatus = 'loading' | 'signedOut' | 'mfa' | 'enroll' | 'ok' | 'offline';

interface AuthCtx {
  status: AuthStatus;
  user: User | null;
  refresh: () => Promise<void>;
  setSignedIn: (user: User, mfa: 'required' | 'enroll' | 'passed') => void;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await endpoints.me();
      setUser(me.user);
      setStatus(me.mfaPassed ? 'ok' : me.user.totpEnabled ? 'mfa' : 'enroll');
    } catch (e) {
      if (isApiError(e) && (e.status === 401 || e.status === 403)) setStatus('signedOut');
      else setStatus('offline');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setAuthHandlers({
      onUnauthenticated: () => {
        setStatus('signedOut');
        qc.clear();
      },
      onMfaRequired: () => setStatus((s) => (s === 'enroll' ? s : 'mfa')),
    });
  }, [qc]);

  const value = useMemo<AuthCtx>(
    () => ({
      status,
      user,
      refresh,
      setSignedIn: (u, mfa) => {
        setUser(u);
        setStatus(mfa === 'passed' ? 'ok' : mfa === 'enroll' ? 'enroll' : 'mfa');
      },
      logout: async () => {
        try {
          await endpoints.logout();
        } finally {
          qc.clear();
          setUser(null);
          setStatus('signedOut');
        }
      },
    }),
    [status, user, refresh, qc],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth outside AuthProvider');
  return c;
}

export const useIsOwner = () => useAuth().user?.role === 'owner';
